import React, { useState } from 'react';
import { View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramTotalResistance } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { formatNumber } from '../../../utils/format';

const KEY = 'tabai.calc.electricalTotalResistance.v1';

type Fields = { r1: string; r2: string; r3: string };
const EMPTY: Fields = { r1: '', r2: '', r3: '' };

/**
 * Total Resistance — Series: RT = R1+R2+...; Parallel: 1/RT = 1/R1+1/R2+...
 */
const ElectricalElectricalSystemsTotalResistanceScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [resistanceType, setResistanceType] = useState('series');
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<string | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = [fields.r1, fields.r2, fields.r3].filter((v) => v !== '');
    if (filled.length < 2) {
      setError('Fill in at least 2 resistance values.');
      return;
    }

    const resistances = filled.map((v) => parseFloat(v));
    if (resistances.some((r) => r < 0)) {
      setError('Resistance values must be non-negative.');
      return;
    }

    if (resistanceType === 'series') {
      setTotal(formatNumber(resistances.reduce((sum, r) => sum + r, 0), { decimals: 4 }));
    } else {
      if (resistances.some((r) => r === 0)) {
        setError('Resistance cannot be zero in a parallel circuit.');
        return;
      }
      const inverseSum = resistances.reduce((sum, r) => sum + 1 / r, 0);
      setTotal(formatNumber(1 / inverseSum, { decimals: 4 }));
    }
  };

  const clearAll = () => {
    setError(null);
    setTotal(null);
    void clearPersisted();
  };

  const result = total
    ? [{ label: 'Total Resistance', value: total, unit: 'Ω', emphasis: true as const }]
    : null;

  const formulaText = resistanceType === 'series'
    ? 'RT = R₁ + R₂ + R₃ + ...'
    : '1/RT = 1/R₁ + 1/R₂ + 1/R₃ + ...';
  const formulaDesc = resistanceType === 'series'
    ? 'Resistances connected in series — total is the sum.'
    : 'Resistances connected in parallel — total is the reciprocal of the sum of reciprocals.';

  return (
    <CalcShell
      title="Total Resistance"
      subtitle="Calculate series or parallel resistance networks"
      formula={
        <>
          <FormulaLine>{formulaText}</FormulaLine>
          <FormulaNote>{formulaDesc}</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result}
      copyFooter={`Formula: ${formulaText}`}
      diagram={<DiagramTotalResistance />}
      outputSlots={{
        resistance_ohms: total ? parseFloat(total) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.resistance_ohms != null && !fields.r1) {
          patch.r1 = values.resistance_ohms.toFixed(4);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <SegmentedButtons
          value={resistanceType}
          onValueChange={setResistanceType}
          buttons={[
            { value: 'series', label: 'Series' },
            { value: 'parallel', label: 'Parallel' },
          ]}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Resistance 1" value={fields.r1} onChangeText={(v) => set({ r1: v })} rightText="Ω" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Resistance 2" value={fields.r2} onChangeText={(v) => set({ r2: v })} rightText="Ω" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Resistance 3 (optional)" value={fields.r3} onChangeText={(v) => set({ r3: v })} rightText="Ω" maxDecimals={4} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default ElectricalElectricalSystemsTotalResistanceScreen;
